import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ChevronLeft } from "lucide-react";
import { api, getErrorMessage } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Toaster, toast } from "sonner";

/**
 * Self-serve password reset — step 1 of 2 (request email).
 * The backend ALWAYS returns the same generic acknowledgement so we never
 * reveal whether an account exists for the submitted address.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // Even hard errors shouldn't leak existence info — show the generic
      // success state unless the request couldn't reach the server at all.
      if (err?.response?.status) setSent(true);
      else toast.error(getErrorMessage(err, "Could not reach the server."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-white border border-[#E2E4E0] rounded-xl p-6 shadow-sm">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-[#5C665A] hover:text-[#1A1D1A] mb-4" data-testid="forgot-back-to-login">
          <ChevronLeft size={12} /> Back to sign in
        </Link>
        <div className="db-h mb-5">
          <h1 className="text-2xl font-bold tracking-tight">Reset password</h1>
          <p className="text-sm text-[#5C665A] mt-1">
            Enter your account email — we'll send a reset link if one exists.
          </p>
        </div>

        {sent ? (
          <div
            data-testid="forgot-success"
            className="rounded-md border border-[#CFE2D4] bg-[#F0F8F2] p-4 text-sm text-[#1B4332]"
          >
            <div className="flex items-start gap-2">
              <Mail size={16} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">Check your inbox.</div>
                <p className="text-[#2D6A4F] mt-1 leading-relaxed">
                  If an account exists for this email, a reset link has been
                  sent. The link expires in 60 minutes and can only be used once.
                </p>
              </div>
            </div>
            <div className="mt-4 flex gap-2 text-xs">
              <button
                type="button"
                className="underline underline-offset-2"
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="forgot-send-again"
              >
                Send another
              </button>
              <span className="text-[#8A9088]">·</span>
              <Link to="/login" className="underline underline-offset-2" data-testid="forgot-return-login">
                Return to sign in
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-form" className="space-y-3">
            <div>
              <Label className="text-xs text-[#5C665A]">Email</Label>
              <Input
                data-testid="forgot-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-10"
              />
            </div>
            <Button
              type="submit"
              disabled={busy || !email}
              data-testid="forgot-submit"
              className="w-full h-10 bg-[#2D6A4F] hover:bg-[#1B4332] text-white"
            >
              {busy ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}
      </div>
      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}
